import { useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CListGroup,
  CListGroupItem,
  CLoadingButton,
  CRow,
  CStepper,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCheckCircle } from "@coreui/icons";
import {
  MAX_NOTES_LENGTH,
  MAX_PRIORITIES,
  MAX_SHORT_ANSWER_LENGTH,
  MAX_SYMBOLS,
  useRequestStrategy,
} from "../../../api/hooks/useStrategyRequests";
import {
  useReferenceLabel,
  useStrategyRequestPriorities,
} from "../../../api/hooks/useReference";
import { routePaths } from "../../../routes/route-paths";

/** CStepper's exposed imperative API. Same shape the checkout and the other wizards use. */
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

/**
 * Asking for a strategy we do not publish.
 *
 * A page rather than a modal, because every other multi-step thing a customer does here is one —
 * checkout, ending a subscription, resuming one, changing an email. Nothing is stored on this side:
 * the request is published to the Back Office, which owns the record.
 *
 * Every field is optional and none is a closed list except what matters most. That is deliberate and
 * it is the point of the feature — a closed list can only offer what we already publish, so it would
 * discard the request most worth having: the one for a market we do not cover.
 */
export function RequestStrategyWizardCoreui() {
  const { t } = useTranslation("tradingStrategies");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stepperRef = useRef<CStepperRef>(null);

  const requestStrategy = useRequestStrategy();
  // Served rather than mirrored: the chips and the values the write endpoint accepts are the same
  // Scr.Enums list, so they cannot drift apart.
  const { data: priorities = [] } = useStrategyRequestPriorities();
  const referenceLabel = useReferenceLabel();

  // Seeded from whatever they were browsing, and editable to anything. Read once: re-reading on every
  // render would undo an edit the moment the URL changed for any other reason.
  const [assetClass, setAssetClass] = useState(
    () => searchParams.get("assetClass") ?? "",
  );
  const [timeframe, setTimeframe] = useState(
    () => searchParams.get("timeframe") ?? "",
  );
  const [symbolText, setSymbolText] = useState("");
  const [symbols, setSymbols] = useState<string[]>([]);
  const [mattersMost, setMattersMost] = useState<number[]>([]);
  const [notes, setNotes] = useState("");

  const addSymbol = () => {
    const next = symbolText.trim();
    if (!next || symbols.length >= MAX_SYMBOLS) return;
    // Case-insensitively deduplicated: naming the same instrument twice is one ask, and the customer
    // should not have to notice they already typed it.
    if (symbols.some((s) => s.toLowerCase() === next.toLowerCase())) {
      setSymbolText("");
      return;
    }
    setSymbols([...symbols, next]);
    setSymbolText("");
  };

  const togglePriority = (id: number) =>
    setMattersMost((prev) =>
      prev.includes(id)
        ? prev.filter((p) => p !== id)
        : prev.length >= MAX_PRIORITIES
          ? prev
          : [...prev, id],
    );

  // The server's rule, mirrored so the customer is not sent to a review step they cannot submit.
  // Matters-most is not on this list on purpose: picking only chips says how you would judge a
  // strategy, not which one you want, and the Back Office refuses a request carrying nothing else.
  const hasAnswer =
    assetClass.trim().length > 0 ||
    timeframe.trim().length > 0 ||
    notes.trim().length > 0 ||
    symbols.length > 0;

  const handleSubmit = async () => {
    try {
      await requestStrategy.mutateAsync({
        assetClass: assetClass.trim() || null,
        timeframe: timeframe.trim() || null,
        symbols,
        mattersMost,
        notes: notes.trim() || null,
      });
      // The stepper's position is the state now: advancing to Done is what says it landed.
      stepperRef.current?.next();
    } catch {
      // Stays on the review step rather than navigating, so nothing typed is lost to a failed send.
    }
  };

  const labelOf = (id: number) => {
    const priority = priorities.find((p) => p.id === id);
    return priority ? referenceLabel(priority) : String(id);
  };

  const steps = [
    {
      label: t("request.steps.what"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("request.steps.what")}</h4>
          </CCardHeader>
          <CCardBody className="d-flex flex-column gap-3">
            <p className="text-body-secondary mb-0">{t("request.what.lead")}</p>

            <CRow xs={{ gutter: 3 }}>
              <CCol md={6}>
                <CFormLabel htmlFor="request-asset-class">
                  {t("request.what.market")}
                </CFormLabel>
                <CFormInput
                  id="request-asset-class"
                  value={assetClass}
                  maxLength={MAX_SHORT_ANSWER_LENGTH}
                  placeholder={t("request.what.marketPlaceholder")}
                  onChange={(e) => setAssetClass(e.target.value)}
                />
              </CCol>
              <CCol md={6}>
                <CFormLabel htmlFor="request-timeframe">
                  {t("request.what.timeframe")}
                </CFormLabel>
                <CFormInput
                  id="request-timeframe"
                  value={timeframe}
                  maxLength={MAX_SHORT_ANSWER_LENGTH}
                  placeholder={t("request.what.timeframePlaceholder")}
                  onChange={(e) => setTimeframe(e.target.value)}
                />
              </CCol>
            </CRow>
            <p className="small text-body-secondary mb-0">
              {t("request.what.prefilled")}
            </p>

            <div>
              <CFormLabel htmlFor="request-symbol">
                {t("request.what.instruments")}
              </CFormLabel>
              <div className="d-flex gap-2">
                <CFormInput
                  id="request-symbol"
                  value={symbolText}
                  maxLength={MAX_SHORT_ANSWER_LENGTH}
                  placeholder={t("request.what.instrumentPlaceholder")}
                  disabled={symbols.length >= MAX_SYMBOLS}
                  onChange={(e) => setSymbolText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    // Otherwise Enter submits the step rather than adding the instrument, which is
                    // what someone typing a list expects it to do.
                    e.preventDefault();
                    addSymbol();
                  }}
                />
                <CButton
                  color="secondary"
                  variant="outline"
                  className="flex-shrink-0"
                  disabled={!symbolText.trim() || symbols.length >= MAX_SYMBOLS}
                  onClick={addSymbol}
                >
                  {t("request.what.add")}
                </CButton>
              </div>
              {symbols.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {symbols.map((symbol) => (
                    <CButton
                      key={symbol}
                      color="primary"
                      variant="outline"
                      size="sm"
                      className="font-monospace"
                      onClick={() =>
                        setSymbols(symbols.filter((s) => s !== symbol))
                      }
                      aria-label={t("request.what.remove", { symbol })}
                    >
                      {symbol} ×
                    </CButton>
                  ))}
                </div>
              )}
              <p className="small text-body-secondary mt-2 mb-0">
                {t("request.what.instrumentsHelp")}
              </p>
            </div>

            <hr className="my-1" />
            <div className="d-flex justify-content-between">
              <CButton
                color="secondary"
                onClick={() => navigate(routePaths.strategies)}
              >
                {t("request.buttons.cancel")}
              </CButton>
              <CButton
                color="primary"
                onClick={() => stepperRef.current?.next()}
              >
                {t("request.buttons.next")}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: t("request.steps.matters"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("request.steps.matters")}</h4>
          </CCardHeader>
          <CCardBody className="d-flex flex-column gap-3">
            <p className="text-body-secondary mb-0">
              {t("request.matters.lead", { max: MAX_PRIORITIES })}
            </p>

            <div className="d-flex flex-wrap gap-2">
              {priorities.map((priority) => {
                const chosen = mattersMost.includes(priority.id);
                return (
                  <CButton
                    key={priority.id}
                    color="primary"
                    variant={chosen ? undefined : "outline"}
                    aria-pressed={chosen}
                    disabled={!chosen && mattersMost.length >= MAX_PRIORITIES}
                    onClick={() => togglePriority(priority.id)}
                  >
                    {referenceLabel(priority)}
                  </CButton>
                );
              })}
            </div>

            <div>
              <CFormLabel htmlFor="request-notes">
                {t("request.matters.notes")}
              </CFormLabel>
              <CFormTextarea
                id="request-notes"
                rows={3}
                value={notes}
                maxLength={MAX_NOTES_LENGTH}
                onChange={(e) => setNotes(e.target.value)}
              />
              <p className="small text-body-secondary mt-2 mb-0">
                {t("request.matters.notesHelp")}
              </p>
            </div>

            <hr className="my-1" />
            <div className="d-flex justify-content-between">
              <CButton
                color="secondary"
                onClick={() => stepperRef.current?.prev()}
              >
                {t("request.buttons.back")}
              </CButton>
              <CButton
                color="primary"
                onClick={() => stepperRef.current?.next()}
              >
                {t("request.buttons.next")}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: t("request.steps.review"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("request.steps.review")}</h4>
          </CCardHeader>
          <CCardBody className="d-flex flex-column gap-3">
            <p className="text-body-secondary mb-0">{t("request.review.lead")}</p>

            <CListGroup>
              <CListGroupItem className="d-flex justify-content-between gap-3">
                <strong>{t("request.what.market")}</strong>
                <span className="text-end">{answerOr(assetClass, t)}</span>
              </CListGroupItem>
              <CListGroupItem className="d-flex justify-content-between gap-3">
                <strong>{t("request.what.timeframe")}</strong>
                <span className="text-end">{answerOr(timeframe, t)}</span>
              </CListGroupItem>
              <CListGroupItem className="d-flex justify-content-between gap-3">
                <strong>{t("request.what.instruments")}</strong>
                <span className="text-end">
                  {symbols.length > 0 ? (
                    <span className="font-monospace">{symbols.join(" · ")}</span>
                  ) : (
                    <span className="text-body-secondary">
                      {t("request.review.notSaid")}
                    </span>
                  )}
                </span>
              </CListGroupItem>
              <CListGroupItem className="d-flex justify-content-between gap-3">
                <strong>{t("request.steps.matters")}</strong>
                <span className="text-end">
                  {mattersMost.length > 0 ? (
                    mattersMost.map(labelOf).join(", ")
                  ) : (
                    <span className="text-body-secondary">
                      {t("request.review.notSaid")}
                    </span>
                  )}
                </span>
              </CListGroupItem>
              <CListGroupItem className="d-flex justify-content-between gap-3">
                <strong>{t("request.matters.notes")}</strong>
                <span className="text-end">{answerOr(notes, t)}</span>
              </CListGroupItem>
            </CListGroup>

            {!hasAnswer && (
              <CAlert color="warning" className="mb-0">
                {t("request.review.nothingSaid")}
              </CAlert>
            )}

            {requestStrategy.isError && (
              <CAlert color="danger" className="mb-0">
                {t("request.review.failed")}
              </CAlert>
            )}

            <p className="small text-body-secondary mb-0">
              {t("request.review.whatNext")}
            </p>

            <hr className="my-1" />
            <div className="d-flex justify-content-between">
              <CButton
                color="secondary"
                disabled={requestStrategy.isPending}
                onClick={() => stepperRef.current?.prev()}
              >
                {t("request.buttons.back")}
              </CButton>
              <CLoadingButton
                color="primary"
                loading={requestStrategy.isPending}
                disabled={!hasAnswer}
                disabledOnLoading
                onClick={handleSubmit}
              >
                {t("request.buttons.send")}
              </CLoadingButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: t("request.steps.done"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("request.sent.title")}</h4>
          </CCardHeader>
          <CCardBody>
            <div className="text-center py-3 mb-2">
              <CIcon
                icon={cilCheckCircle}
                size="3xl"
                className="text-success mb-3"
              />
              <h3 className="mb-2">{t("request.sent.headline")}</h3>
              <p
                className="text-body-secondary mx-auto"
                style={{ maxWidth: 480 }}
              >
                {t("request.sent.body")}
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2 justify-content-center">
              <Link
                to={routePaths.strategies}
                className="btn btn-primary"
              >
                {t("request.sent.browse")}
              </Link>
              <CButton
                color="secondary"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                {t("request.sent.another")}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>{t("request.header")}</h2>
      <p className="text-body-secondary">
        {t("request.description")}{" "}
        <Link to={routePaths.strategies}>
          {t("request.backToBrowsing")}
        </Link>
        .
      </p>
      <hr />

      <CStepper ref={stepperRef} steps={steps} />
    </>
  );
}

/** An unanswered field is a real answer here — it means they did not mind — so it says so. */
function answerOr(value: string, t: (key: string) => string) {
  return value.trim().length > 0 ? (
    value.trim()
  ) : (
    <span className="text-body-secondary">{t("request.review.notSaid")}</span>
  );
}
